<script setup lang="ts">
import {EMPTY_PART} from "../ComponentResolver";
import {defineProps, ref, computed, type CSSProperties} from "vue";
import useContainer, {type ContainerProps, containerProps} from "./Container";
import {injectWidgetDisplay} from "../PartInjectKeys";
import {injectEnv} from "../Environment";
import MoreOpener from "../helpers/MoreOpener.vue";
import ShowDown from "../helpers/ShowDown.vue";
import ApiError from "../helpers/ApiError.vue";
import type {Widget} from "@t/fd/layout/Widget";
import type {ConcreteDisplay, DisplayRule} from "@t/fd/layout/FormLayout";
import WidgetErrors from "@fd/containers/WidgetErrors.vue";
import FormHelp from "@fd/components/FormHelp.vue";

const uprops = defineProps({
    ...containerProps,
});

const props = uprops as ContainerProps;

const widget = computed<Widget>(() => props.part as Widget);

const {componentOfPart, partRef, localData, ro, vm, visible, errors, clazz, style, myDisplayRule} = useContainer(props);
const DISPLAY_CLASSES = new Set().add("input").add("area").add("option").add("switch");
const editorOpen = ref(false);
const pickingComponent = ref(null);
const env = injectEnv();
const widgetDisplay: DisplayRule = myDisplayRule ?? injectWidgetDisplay(env);

const hasLabel = computed<boolean>(() => !!widget.value.label);
const hasField = computed<boolean>(() => !!widget.value.field);
const hasEditor = computed<boolean>(() => !!widget.value.editor);
const hasHelp = computed<boolean>(() => !!widget.value.help);
const flexClass = computed<string>(() => {
	return "app-"+displayType.value;
});

const labelPart = computed(() => {
    return widget.value.label || EMPTY_PART;
});
const fieldPart = computed(() => {
    return widget.value.field || EMPTY_PART;
});
const editorPart = computed(() => {
    return widget.value.editor || EMPTY_PART;
});
const colonSuffix = computed(() => {
    if (widgetDisplay.colon && widget.value.label) return ":";
    return "";
});
const labelStyle = computed<CSSProperties>(() => {
    const s: CSSProperties = {};
    if (widgetDisplay.label) {
        s.textTransform = widgetDisplay.label;
    }
    return s;
});
const displayType = computed<ConcreteDisplay | undefined>(() => {
    if (!widget.value.display) {
        return widgetDisplay.input;
    } else {
        switch (widget.value.display) {
            case "input":
                return widgetDisplay.input;
            case "area":
                return widgetDisplay.area;
            case "option":
                return widgetDisplay.option;
            case "switch":
                return widgetDisplay.switch;
            default:
                return widget.value.display as ConcreteDisplay;
        }
    }
});

function widgetClass(...clss: string[]) {
    const block = [];
    block.push(...clss);
	block.push("display-" + displayType.value);
    if (widget.value.bigger) block.push("bigger");
    if (errors.value.length > 0) block.push("fd-widget-with-errors");
	if (clazz.value) block.push(clazz.value);
    return block;
}

function toggleOpenableTouch() {
    if (env.touch) editorOpen.value = !editorOpen.value;  // touch devices open editor on click
}
function toggleOpenableMouse() {
    if (!env.touch) editorOpen.value = !editorOpen.value;
}

function pickMe() {
    if (pickingComponent.value != null) {
        let pc = pickingComponent.value;
        // @ts-ignore
        pc.invokePicker(partRef(labelPart.value));
    }
}
</script>
<!--
This part supports following types of display (see `display` property):

standard
  | label | field       |
stacked
  | label               |
  | field               |
checkbox-push
  |       | field label |
stacked-push
  |       | label       |
  |       | field       |
checkbox-pull
  | field label         |
flow
  | label field         |
flow-reverse
  | field label         |
spaced
  | label         field |
spaced-reverse
  | field         label |
field
  | field               |
field-push
  |       | field       |
openable
  | label         field |
  |---------------------|	<-- secondary editor is opened usually by clicking on the first line
  | editor              |

checkbox-inline
  field label       <-- without enclosing table
input-inline
  label field       <-- without enclosing table
-->
<template>
	<template v-if="!visible"></template>
	<template v-else>
		<div v-if="displayType === 'standard'" class="fd-widget" :class="widgetClass()" :style="style">
			<widget-errors :errors="errors"></widget-errors>
			<div v-if="hasLabel" class="pos-label app-label"><component
				:is="componentOfPart(labelPart)"
				:part="partRef(labelPart)"
				:data="localData"
				:readonly="ro"
				:viewmode="vm"
				:style="labelStyle"></component>{{ colonSuffix }}<form-help v-if="hasHelp" for="lab" :dt="displayType" :value="widget.help"></form-help></div>
			<div v-if="hasField" class="pos-field app-field"><component :is="componentOfPart(fieldPart)" :part="partRef(fieldPart)" :data="localData" :readonly="ro" :viewmode="vm"></component><form-help v-if="hasHelp" :for="['fld','dir']" :dt="displayType" :value="widget.help"></form-help></div>
			<div v-if="hasEditor" class="pos-wide app-editor"></div>
		</div>
		<div v-else-if="displayType === 'stacked' || displayType === 'stacked-push'" class="fd-widget" :class="widgetClass()" :style="style">
			<widget-errors :errors="errors"></widget-errors>
			<div class="app-stacked" :class="displayType === 'stacked-push' ? 'pos-field':'pos-wide'">
				<div v-if="hasLabel" class="app-label">
					<component
						:is="componentOfPart(labelPart)"
						:part="partRef(labelPart)"
						:data="localData"
						:readonly="ro"
						:viewmode="vm"
						:style="labelStyle"></component>{{ colonSuffix }}<form-help v-if="hasHelp" for="lab" :dt="displayType" :value="widget.help" :above="true"></form-help>
				</div>
				<div v-if="hasField" class="app-field">
					<component :is="componentOfPart(fieldPart)" :part="partRef(fieldPart)" :data="localData" :readonly="ro" :viewmode="vm"></component>
					<form-help v-if="hasHelp" for="fld" :dt="displayType" :value="widget.help"></form-help>
				</div>
				<form-help v-if="hasHelp" for="dir" :dt="displayType" :value="widget.help" class="fh-no-margin"></form-help>
			</div>
		</div>
		<div v-else-if="displayType === 'checkbox-push' || displayType === 'checkbox-pull'" class="fd-widget" :class="widgetClass()" :style="style">
			<widget-errors :errors="errors"></widget-errors>
			<label class="app-checkbox" :class="displayType === 'checkbox-push' ? 'pos-field':'pos-wide'">
				<component :is="componentOfPart(fieldPart)" :part="partRef(fieldPart)" :data="localData" :readonly="ro" :viewmode="vm"></component>
				<div><form-help v-if="hasHelp" for="fld" :dt="displayType" :value="widget.help" margin="right"></form-help>
				<component
					:is="componentOfPart(labelPart)"
					:part="partRef(labelPart)"
					:data="localData"
					:readonly="ro"
					:viewmode="vm"
					:style="labelStyle" class="fd-checkbox-label"></component><form-help v-if="hasHelp" :for="['lab','dir']" :dt="displayType" :value="widget.help" class="fh-no-margin"></form-help></div>
			</label>
		</div>
		<div v-else-if="displayType === 'spaced' || displayType === 'spaced-reverse' || displayType === 'flow' || displayType === 'flow-reverse'" class="fd-widget" :class="widgetClass()" :style="style">
			<div class="pos-wide" :class="flexClass">
				<label class="app-label">
					<component
						:is="componentOfPart(labelPart)"
						:part="partRef(labelPart)"
						:data="localData"
						:readonly="ro"
						:viewmode="vm"
						:style="labelStyle"></component>{{ colonSuffix }}<form-help v-if="hasHelp" for="lab" :dt="displayType" :value="widget.help"></form-help></label>
				<span><component :is="componentOfPart(fieldPart)" :part="partRef(fieldPart)" :data="localData" :readonly="ro" :viewmode="vm"></component><form-help v-if="hasHelp" for="fld" :dt="displayType" :value="widget.help"></form-help></span>
			</div>
		</div>
		<div v-else-if="displayType === 'field' || displayType === 'field-push'" class="fd-widget" :class="widgetClass()" :style="style">
			<widget-errors :errors="errors"></widget-errors>
			<div class="app-field" :class="displayType === 'field-push' ? 'pos-field' : 'pos-wide'">
				<component :is="componentOfPart(fieldPart)" :part="partRef(fieldPart)" :data="localData" :readonly="ro" :viewmode="vm"></component>
				<form-help v-if="hasHelp" for="fld" :dt="displayType" :value="widget.help"></form-help>
			</div>
		</div>
		<div v-else-if="displayType === 'openable'" class="fd-widget" :class="widgetClass()" @click.prevent.stop="toggleOpenableTouch" :style="style">
			<div class="pos-wide app-spaced">
				<span style="position:relative;left: -4px"><more-opener v-if="!env.touch" v-model="editorOpen" :left="true"></more-opener><label class="fd-label" style="cursor:pointer" @click.prevent.stop="toggleOpenableMouse">
					<component
						:is="componentOfPart(labelPart)"
						:part="partRef(labelPart)"
						:data="localData"
						:readonly="ro"
						:viewmode="vm"
						:style="labelStyle"></component>
				  </label></span>
				<component :is="componentOfPart(fieldPart)" :part="partRef(fieldPart)" :data="localData" :readonly="ro" :viewmode="vm"></component>
			</div>
			<show-down :show="editorOpen" class="pos-wide">
				<component
					:is="componentOfPart(editorPart)"
					:part="partRef(editorPart)"
					:data="localData"
					:readonly="ro"
					:viewmode="vm"></component>
			</show-down>
		</div>
		<div v-else-if="displayType === 'picked'" class="fd-widget" :class="widgetClass()" :style="style">
			<div class="pos-wide app-spaced" :class="{ chevron: !ro }" @click.prevent.stop="pickMe">
				<label class="app-label">
					<component
						:is="componentOfPart(labelPart)"
						:part="partRef(labelPart)"
						:data="localData"
						:readonly="ro"
						:viewmode="vm"
						:style="labelStyle"></component>{{ colonSuffix }}
				</label>
				<div class="app-field">
					<component
						:is="componentOfPart(fieldPart)"
						:part="partRef(fieldPart)"
						:data="localData"
						:readonly="ro"
						:viewmode="vm"
						ref="pickingComponent"></component>
				</div>
			</div>
		</div>
		<label v-else-if="displayType === 'checkbox-inline'" class="app-checkbox" :class="widgetClass()" :style="style">
			<component :is="componentOfPart(fieldPart)" :part="partRef(fieldPart)" :data="localData" :readonly="ro" :viewmode="vm"></component>
			<span><form-help v-if="hasHelp" for="fld" :dt="displayType" :value="widget.help" margin="right"></form-help>
				<component
					:is="componentOfPart(labelPart)"
					:part="partRef(labelPart)"
					:data="localData"
					:readonly="ro"
					:viewmode="vm"
					:style="labelStyle" class="fd-checkbox-label"></component><form-help v-if="hasHelp" :for="['lab','dir']" :dt="displayType" :value="widget.help" class="fh-no-margin"></form-help></span>
		</label>
		<span v-else-if="displayType === 'input-inline'" class="app-inline" :class="widgetClass()" :style="style">
			<component
				:is="componentOfPart(labelPart)"
				:part="partRef(labelPart)"
				:data="localData"
				:readonly="ro"
				:viewmode="vm"
				:style="labelStyle" class="fd-checkbox-label"></component>{{ colonSuffix }}<form-help v-if="hasHelp" for="lab" :dt="displayType" :value="widget.help"></form-help>
			<component :is="componentOfPart(fieldPart)" :part="partRef(fieldPart)" :data="localData" :readonly="ro" :viewmode="vm"></component>
			<form-help v-if="hasHelp" :for="['fld','dir']" :dt="displayType" :value="widget.help" class="fh-no-margin"></form-help>
		</span>

		<div v-else style="color: red" class="pos-wide">
			Invalid part display property <code>{{ displayType }}</code>
		</div>

	</template>
</template>

<style>
/** All widget are enclosed in this */
.fd-widget {
	display: contents;
	padding-top: 4px;
}
/**
 * Following classes are divided into two categories:
 * pos-*  for positioning
 * app-*  for appearances
 */
.pos-label,
.pos-field,
.pos-wide {
}

.pos-label {
	grid-column: var(--label-start) / var(--label-end);
}
.pos-field {
	grid-column: var(--field-start) / var(--field-end);
}
.pos-wide {
	grid-column: var(--widget-start) / var(--widget-end);
}
.app-label,
.app-field {
	line-height: var(--input-height);	/* to align labels and fields baselines */
}
.bigger .app-label {
	font-weight: bold;
}
.app-field {
	display: flex;
	flex-direction: row;
	align-items: baseline;
	/**** NOT SURE WE NEED THIS
	& > input,
	& > select {
		max-width: 100%;
	}*/
}
.flexy {
	flex-grow: 1;
	flex-shrink: 1;
	flex-basis: auto;
}

.app-stacked {
	display: flex;
	flex-direction: column;
	row-gap: var(--form-v-padding);
}
.app-checkbox {
	display: grid;
	grid-template-columns: 1.5rem 1fr;
	justify-self: start;	/* Do not take the whole grid cell (too much clickable space for the checkbox) */
	align-items: baseline;	/* Proper checkbox and label alignment */
	user-select: none;
	-webkit-user-select: none;
}
/* inline input */
.app-inline {
	display: flex;
	flex-direction: row;
	align-items: baseline;
	column-gap: var(--input-spacing);
	row-gap: 4px;
}

.app-spaced,
.app-spaced-reverse,
.app-flow,
.app-flow-reverse {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: baseline;
	column-gap: calc(2 * var(--form-h-padding));
}
.app-spaced-reverse,
.app-flow-reverse {
	flex-direction: row-reverse;
}
.app-flow {
	justify-content: flex-start;
}
.app-flow-reverse {
	justify-content: flex-end;
}


.fd-label {
	--fg: var(--label-fg);
	color: var(--fg);
	font-size: var(--label-font-size);
}
.fd-checkbox-label {
	--fg: var(--checkbox-label-fg);
	color: var(--fg);
	font-size: var(--checkbox-label-font-size);
}

/** Used to separate two or more items in one row side-to-side */

.fd-spaced {
	align-items: center;
	justify-content: space-between;
	line-height: 30px; /* in sync with switch height */
	padding-right: var(--gap);
	padding-top: var(--gap);
	padding-bottom: var(--gap);
}

.fd-stacked {
	flex-direction: column;
	align-items: stretch;
}

/** Openable **/
.fd-openable {
	display: flex;
	flex-direction: column;
}

.fd-openable-row {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: stretch;
	column-gap: 4px;
	line-height: 30px; /* in sync with switch height */
	width: 100%;
	padding-right: var(--gap);
	padding-top: var(--gap);
	padding-bottom: var(--gap);
}

.fd-editor {
	padding-top: var(--gap);
	padding-bottom: var(--gap);
	border-top: var(--hair) solid var(--border-color);
	box-sizing: border-box;
}

.fd-picked-row {
	position: relative;
}

.fd-picked-row.chevron {
	padding-right: var(--gap-chevron);
}

/* Slide-down animation */
.slide-down-enter-active,
.slide-down-leave-active {
	transition: max-height 200ms ease-in-out;
}

.slide-down-enter-to,
.slide-down-leave-from {
	overflow: hidden;
	max-height: 256px;
}

.slide-down-enter-from,
.slide-down-leave-to {
	overflow: hidden;
	max-height: 0;
}

</style>

<!--template v-else>
	<label v-else-if="displayType === 'checkbox-inline'" :class="widgetClass()">
		<component :is="componentOfPart(fieldPart)" :part="partRef(fieldPart)" :data="localData" :readonly="ro" :viewmode="vm"
			style="margin-right:var(--input-spacing)"></component>
		<component
			:is="componentOfPart(labelPart)"
			:part="partRef(labelPart)"
			:data="localData"
			:readonly="ro"
			:viewmode="vm" class="fd-checkbox-label"></component>
	</label>
	<span v-else-if="displayType === 'input-inline'" :class="widgetClass()">
		<component
			:is="componentOfPart(labelPart)"
			:part="partRef(labelPart)"
			:data="localData"
			:readonly="ro"
			:viewmode="vm" class="fd-label"></component>{{ colonSuffix }}
		<component :is="componentOfPart(fieldPart)" :part="partRef(fieldPart)" :data="localData" :readonly="ro" :viewmode="vm"
			style="margin-right:var(--input-spacing)"></component>
	</span>
</template-->
